import { useNode, useEditor } from "@craftjs/core";
import React, { useState, useEffect } from "react";
import { Box, Typography, Menu, MenuItem } from "@mui/material";
import Cookies from "js-cookie";
import { selectedOutlineSx, itemCardStyle } from "../../config/settingsStyles";
import { TextField, SelectField, SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";
import { useCMSConfig } from "../../context/CMSConfigContext";

export interface LineLoginButtonProps {
  // LINE Configuration
  liffId?: string;
  apiHost?: string;
  appName?: string;

  // Appearance
  buttonText?: string;
  loggedInText?: string;
  style?: "green" | "white";
  size?: "small" | "medium" | "large";
  borderRadius?: number;

  // Behavior
  redirectUrl?: string;
  showUserName?: boolean;
}

// LINE Official Colors
const LINE_GREEN = "#06C755";
const LINE_GREEN_HOVER = "#05b54d";

export const LineLoginButton = ({
  liffId: liffIdProp = "",
  apiHost = "",
  appName = "",
  buttonText = "เข้าสู่ระบบด้วย LINE",
  loggedInText = "สวัสดี",
  style = "green",
  size = "medium",
  borderRadius = 8,
  redirectUrl = "",
  showUserName = true,
}: LineLoginButtonProps) => {
  const { lineLiffId } = useCMSConfig();
  const liffId = liffIdProp || lineLiffId || "";

  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { enabled: editorEnabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  // User state from cookies
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userPicture, setUserPicture] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Check login status from cookies
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = Cookies.get("line-api-token");
      const name = Cookies.get("line-display-name");
      const picture = Cookies.get("line-picture-url");

      setIsLoggedIn(!!token);
      setUserName(name || "");
      setUserPicture(picture || "");
    };

    checkLoginStatus();

    // Listen for storage/cookie changes
    const interval = setInterval(checkLoginStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (editorEnabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (isLoggedIn) {
      setAnchorEl(e.currentTarget);
    } else {
      // In production, this would trigger LIFF login
      // For now, show a message about configuration
      if (!liffId) {
        alert("กรุณาตั้งค่า LIFF ID ใน Settings");
        return;
      }

      // The actual login will be handled by exported JavaScript
    }
  };

  const handleLogout = () => {
    Cookies.remove("line-user-id");
    Cookies.remove("line-api-token");
    Cookies.remove("line-display-name");
    Cookies.remove("line-picture-url");
    setIsLoggedIn(false);
    setUserName("");
    setUserPicture("");
    setAnchorEl(null);
    window.location.reload();
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Size configurations
  const sizeConfig = {
    small: { px: 12, py: 6, fontSize: 12, iconSize: 16 },
    medium: { px: 16, py: 8, fontSize: 14, iconSize: 20 },
    large: { px: 24, py: 12, fontSize: 16, iconSize: 24 },
  };

  const config = sizeConfig[size];

  // Style configurations
  const styleConfig = {
    green: {
      backgroundColor: LINE_GREEN,
      color: "#ffffff",
      hoverBg: LINE_GREEN_HOVER,
      border: "none",
    },
    white: {
      backgroundColor: "#ffffff",
      color: LINE_GREEN,
      hoverBg: "#f5f5f5",
      border: `2px solid ${LINE_GREEN}`,
    },
  };

  const currentStyle = styleConfig[style];

  // LINE Logo SVG
  const LineLogo = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={config.iconSize}
      height={config.iconSize}
      fill="currentColor"
    >
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.349 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  );

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        flexShrink: 0,
        ...(selected && { ...selectedOutlineSx, outlineOffset: "2px" }),
      }}
    >
      <Box
        component="button"
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: `${config.px}px`,
          py: `${config.py}px`,
          fontSize: `${config.fontSize}px`,
          fontWeight: 600,
          fontFamily: "inherit",
          borderRadius: `${borderRadius}px`,
          cursor: editorEnabled ? "default" : "pointer",
          transition: "all 0.2s",
          whiteSpace: "nowrap",
          backgroundColor: currentStyle.backgroundColor,
          color: currentStyle.color,
          border: currentStyle.border,
          "&:hover": {
            backgroundColor: editorEnabled ? currentStyle.backgroundColor : currentStyle.hoverBg,
          },
        }}
      >
        {isLoggedIn && showUserName ? (
          <>
            {userPicture ? (
              <Box
                component="img"
                src={userPicture}
                alt={userName}
                sx={{
                  width: config.iconSize + 4,
                  height: config.iconSize + 4,
                  borderRadius: "50%",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: config.iconSize + 4,
                  height: config.iconSize + 4,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: config.fontSize - 2,
                }}
              >
                👤
              </Box>
            )}
            <span>
              {loggedInText}, {userName}
            </span>
            <span style={{ marginLeft: 4 }}>▼</span>
          </>
        ) : (
          <>
            <LineLogo />
            <span>{buttonText}</span>
          </>
        )}
      </Box>

      {/* Logout Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleLogout}>🚪 ออกจากระบบ</MenuItem>
      </Menu>

      {/* Editor hint when not configured */}
      {editorEnabled && !liffId && (
        <Typography
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            mt: 0.5,
            fontSize: "0.7rem",
            color: "warning.main",
            whiteSpace: "nowrap",
          }}
        >
          ⚠️ ต้องตั้งค่า LIFF ID
        </Typography>
      )}
    </Box>
  );
};

export const LineLoginButtonSettings = () => {
  const {
    actions: { setProp },
    liffId,
    apiHost,
    appName,
    buttonText,
    loggedInText,
    style,
    size,
    borderRadius,
    redirectUrl,
    showUserName,
  } = useNode((node) => ({
    liffId: node.data.props.liffId,
    apiHost: node.data.props.apiHost,
    appName: node.data.props.appName,
    buttonText: node.data.props.buttonText,
    loggedInText: node.data.props.loggedInText,
    style: node.data.props.style,
    size: node.data.props.size,
    borderRadius: node.data.props.borderRadius,
    redirectUrl: node.data.props.redirectUrl,
    showUserName: node.data.props.showUserName,
  }));

  return (
    <SettingsSection>
      {/* LINE Configuration */}
      <div style={itemCardStyle}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: LINE_GREEN, fontWeight: 600 }}>
          🟢 LINE Configuration
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <TextField
            label="LIFF ID *"
            value={liffId}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.liffId = v))}
            placeholder="2006xxxxxx-xxxxxxxx"
          />

          <TextField
            label="API Host *"
            value={apiHost}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.apiHost = v))}
            placeholder="https://api.example.com"
          />

          <TextField
            label="App Name"
            value={appName}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.appName = v))}
            placeholder="myapp"
          />
        </div>
      </div>

      {/* Appearance */}
      <div style={itemCardStyle}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          🎨 Appearance
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <TextField
            label="Button Text"
            value={buttonText}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.buttonText = v))}
          />

          <div>
            <label
              style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, marginBottom: "4px", color: "#374151" }}
            >
              Style
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setProp((props: LineLoginButtonProps) => (props.style = "green"))}
                style={{
                  flex: 1,
                  padding: "8px",
                  backgroundColor: style === "green" ? LINE_GREEN : "#e5e7eb",
                  color: style === "green" ? "#fff" : "#374151",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: style === "green" ? 600 : 400,
                }}
              >
                Green
              </button>
              <button
                onClick={() => setProp((props: LineLoginButtonProps) => (props.style = "white"))}
                style={{
                  flex: 1,
                  padding: "8px",
                  backgroundColor: style === "white" ? "#fff" : "#e5e7eb",
                  color: style === "white" ? LINE_GREEN : "#374151",
                  border: style === "white" ? `2px solid ${LINE_GREEN}` : "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: style === "white" ? 600 : 400,
                }}
              >
                White
              </button>
            </div>
          </div>

          <SelectField
            label="Size"
            value={size}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.size = v as "small" | "medium" | "large"))}
            options={[
              { value: "small", label: "Small" },
              { value: "medium", label: "Medium" },
              { value: "large", label: "Large" },
            ]}
          />

          <SliderField
            label="Border Radius"
            value={borderRadius}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.borderRadius = v))}
            min={0}
            max={24}
            unit="px"
          />
        </div>
      </div>

      {/* After Login */}
      <div style={itemCardStyle}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          👤 After Login
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <TextField
            label="Redirect URL (optional)"
            value={redirectUrl}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.redirectUrl = v))}
            placeholder="/dashboard หรือเว้นว่างเพื่ออยู่หน้าเดิม"
          />

          <TextField
            label="Logged In Text"
            value={loggedInText}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.loggedInText = v))}
          />

          <CheckboxField
            label="แสดงชื่อผู้ใช้หลัง Login"
            checked={showUserName}
            onChange={(v) => setProp((props: LineLoginButtonProps) => (props.showUserName = v))}
          />
        </div>
      </div>
    </SettingsSection>
  );
};

LineLoginButton.craft = {
  displayName: "LINE Login",
  props: {
    liffId: "",
    apiHost: "",
    appName: "",
    buttonText: "เข้าสู่ระบบด้วย LINE",
    loggedInText: "สวัสดี",
    style: "green",
    size: "medium",
    borderRadius: 8,
    redirectUrl: "",
    showUserName: true,
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: LineLoginButtonSettings,
  },
};
